import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Descriptions, Tag, Button, Input, message, Spin, Timeline, Space } from 'antd';
import { ArrowLeftOutlined, SendOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ticketsApi } from '../../services/api';

const { TextArea } = Input;

const statusColors: Record<string, string> = {
  open: 'blue',
  in_progress: 'orange',
  resolved: 'green',
  closed: 'default',
};

const priorityColors: Record<string, string> = {
  low: 'default',
  medium: 'blue',
  high: 'orange',
  urgent: 'red',
};

export default function TicketDetailPage() {
  const { t } = useTranslation('support');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [replyText, setReplyText] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['ticket', id],
    queryFn: () => ticketsApi.getTicket(id!),
    enabled: !!id,
  });

  const replyMutation = useMutation({
    mutationFn: (message: string) => ticketsApi.addReply(id!, { message }),
    onSuccess: () => {
      message.success(t('detail_page.reply_success'));
      setReplyText('');
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || t('detail_page.reply_failed'));
    },
  });

  const handleReply = () => {
    if (!replyText.trim()) {
      message.warning(t('detail_page.reply_empty'));
      return;
    }
    replyMutation.mutate(replyText);
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 50 }}>
        <Spin size="large" />
      </div>
    );
  }

  const ticket = data?.data;

  if (!ticket) {
    return <div>{t('detail_page.not_found')}</div>;
  }

  return (
    <div>
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/support')}
        style={{ marginBottom: 16 }}
      >
        {t('detail_page.back')}
      </Button>

      <Card title={ticket.subject} style={{ marginBottom: 16 }}>
        <Descriptions column={2}>
          <Descriptions.Item label={t('columns.status')}>
            <Tag color={statusColors[ticket.status]}>
              {t(`status.${ticket.status}`) || ticket.status}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t('columns.priority')}>
            <Tag color={priorityColors[ticket.priority]}>
              {t(`priority.${ticket.priority}`) || ticket.priority}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t('detail_page.category')}>{ticket.category || '-'}</Descriptions.Item>
          <Descriptions.Item label={t('detail_page.created_at')}>
            {new Date(ticket.created_at).toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label={t('detail_page.description')} span={2}>
            <div style={{ whiteSpace: 'pre-wrap' }}>{ticket.description}</div>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title={t('detail_page.messages')} style={{ marginBottom: 16 }}>
        {ticket.messages && ticket.messages.length > 0 ? (
          <Timeline>
            {ticket.messages.map((msg: any) => (
              <Timeline.Item key={msg.id} color={msg.admin_id ? 'green' : 'blue'}>
                <div>
                  <Space>
                    <strong>{msg.admin_id ? t('detail_page.support') : t('detail_page.me')}</strong>
                    <span style={{ color: '#999', fontSize: 12 }}>
                      {new Date(msg.created_at).toLocaleString()}
                    </span>
                  </Space>
                  <div style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>{msg.message}</div>
                </div>
              </Timeline.Item>
            ))}
          </Timeline>
        ) : (
          <div style={{ textAlign: 'center', color: '#999', padding: 20 }}>{t('detail_page.no_messages')}</div>
        )}
      </Card>

      {ticket.status !== 'closed' && (
        <Card title={t('detail_page.add_reply')}>
          <TextArea
            rows={4}
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder={t('detail_page.reply_placeholder')}
            style={{ marginBottom: 16 }}
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={handleReply}
            loading={replyMutation.isPending}
          >
            {t('detail_page.send')}
          </Button>
        </Card>
      )}
    </div>
  );
}
